import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * Safety margin added to the measured required width. Gap/margin constants
 * below are estimates of the single-line layout chrome — the slack makes the
 * decision err towards breaking into two lines too early (harmless) rather
 * than too late (overlap).
 */
export const FIT_SLACK = 24

/**
 * Extra room required before switching BACK from two lines to one. Without
 * it a row sitting exactly at the edge would flip layouts on every
 * measurement (the two layouts themselves change what gets measured).
 */
export const FIT_HYSTERESIS = 16

/** __main gaps in the single-line layout (3 joins à 10px, play margin −4) + play-button reserve (24px, reserved even when hidden so hover doesn't reflow). */
const FIXED_ROW_CHROME = 26 + 24

/** margin-right of each title-icon badge (attention / start). */
const TITLE_ICON_MARGIN = 8

/** margin-left of the tag-pill cluster (single-line layout; 0 in the meta row). */
const TAGS_MARGIN = 8

/** Flex gap between the clusters of the two-line meta row. */
const META_GAP = 4

/** Gap between the recurrence mark's arrow and its rhythm text (`__collapsed-badge-trigger`, 4px). */
const RECURRENCE_TEXT_GAP = 4

/** Second measurement delay: framer badge width animations run 0.22s. */
const ANIMATION_SETTLE_MS = 300

/**
 * Minimum number of title characters that must stay visible. The title MAY
 * ellipsize on a single line — only when fewer than this many characters
 * would remain does the row break into two lines.
 */
export const MIN_TITLE_CHARS = 10

/** Reserved width for the ellipsis glyph when the title truncates. */
const ELLIPSIS_WIDTH = 12

/**
 * Pure fit decision: would the classic single-line layout hold the
 * required title width plus all badge clusters? `currentlyTwoLine`
 * applies the switch-back hysteresis.
 */
export function fitsSingleLine(
  availableWidth: number,
  requiredWidth: number,
  currentlyTwoLine: boolean,
): boolean {
  const needed = requiredWidth + FIT_SLACK + (currentlyTwoLine ? FIT_HYSTERESIS : 0)
  return needed <= availableWidth
}

export interface RowLayoutMeasurements {
  /** Full row width (__main). */
  available: number
  /** Everything except title and tags: check column + title-icon badges (incl. margins) + project badge + timings + fixed chrome. */
  base: number
  /** Required title width — full text, or its MIN_TITLE_CHARS minimum for longer titles. */
  titleRequired: number
  /** Natural width of the whole title — the rhythm text only shows when THIS fits beside it. */
  titleFull: number
  /** Natural width of ALL tag pills side by side (0 = no tags). */
  tagsFull: number
  /** Natural width of the summary pill ("Work +2"); equals tagsFull for 0–1 tags. */
  tagsSummary: number
  /** Natural width of the timings cluster (recurrence arrow + deadline + duration), WITHOUT the rhythm text. */
  timings: number
  /** Row-2 capacity for the two-line tag decision. */
  row2Available: number
  /** Extra width of the recurrence mark's rhythm text incl. its gap (0 = the mark carries no text). */
  recurrenceText: number
}

export interface CollapsedRowLayout {
  twoLine: boolean
  summarizeTags: boolean
  /** `true` = the recurrence mark may show its rhythm text (irrelevant without such a mark). */
  recurrenceText: boolean
}

/**
 * Pure layout ladder, preferring the least degraded presentation:
 *
 * 0. single line, the FULL title, all tags and the recurrence mark's rhythm
 *    text (#283) — the text is the first thing to go, before the title
 *    ellipsizes or tags summarize
 * 1. single line, ALL tags individually
 * 2. single line, tags collapsed into one summary pill
 * 3. two lines — tags individually when row 2 holds them, summary otherwise
 *
 * From step 1 on the title counts with its MIN_TITLE_CHARS minimum (it may
 * ellipsize before tags get summarized or the row breaks).
 * `currentlyWithoutText` applies the switch-back hysteresis to step 0.
 */
export function computeRowLayout(
  m: RowLayoutMeasurements,
  currentlyTwoLine: boolean,
  currentlyWithoutText = false,
): CollapsedRowLayout {
  const withMargin = (tagsWidth: number) => (tagsWidth > 0 ? tagsWidth + TAGS_MARGIN : 0)

  if (!currentlyTwoLine && fitsSingleLine(m.available, m.base + m.titleFull + withMargin(m.tagsFull) + m.recurrenceText, currentlyWithoutText)) {
    return { twoLine: false, summarizeTags: false, recurrenceText: true }
  }
  if (fitsSingleLine(m.available, m.base + m.titleRequired + withMargin(m.tagsFull), currentlyTwoLine)) {
    return { twoLine: false, summarizeTags: false, recurrenceText: false }
  }
  if (fitsSingleLine(m.available, m.base + m.titleRequired + withMargin(m.tagsSummary), currentlyTwoLine)) {
    return { twoLine: false, summarizeTags: true, recurrenceText: false }
  }
  const row2Needed = m.tagsFull + META_GAP + m.timings + FIT_SLACK
  return { twoLine: true, summarizeTags: m.tagsFull > 0 && row2Needed > m.row2Available, recurrenceText: false }
}

export interface CollapsedRowFit {
  /** `true` = the row fits on a single line (classic layout). */
  fits: boolean
  /** `true` = 2+ tags collapse into one summary pill ("Work +2"). */
  summarizeTags: boolean
  /** `true` = the recurrence mark may show its rhythm text ("Jeden 1. Mo", #283). */
  recurrenceText: boolean
}

const DEFAULT_FIT: CollapsedRowFit = { fits: true, summarizeTags: false, recurrenceText: true }

/**
 * Measures how a collapsed TaskEntry row should lay out: single line vs
 * two lines, and whether the tag pills collapse into a summary pill.
 *
 * Works in EITHER currently rendered layout: the decision uses only
 * natural, state-independent widths — the `__title-text` inline span
 * (keeps its full width even while the outer `__title` ellipsizes;
 * titles longer than MIN_TITLE_CHARS only count with their 10-character
 * minimum), the hidden `[data-tags-measure]` pill measurers (full list
 * and summary variant), and the badge clusters that render identically
 * in both layouts.
 *
 * Guards:
 * - width 0 (hidden TabStack tab, `display: none`) → keep the last
 *   decision, never decide from a zero measurement.
 * - a shrunk timings cluster in the two-line meta row (scrollWidth >
 *   clientWidth) means even row 2 is space-constrained → stays two-line
 *   + summarized without trusting the shrunk widths.
 * - re-measures via ResizeObserver on `__main`, on `contentKey` changes,
 *   and once more after framer badge-width animations settle.
 *
 * @param contentKey serialized fingerprint of everything that affects
 *   measured widths (title, badge values, tag names, date rollover).
 */
export function useCollapsedRowFit(
  entryRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean,
  contentKey: string,
): CollapsedRowFit {
  const [fit, setFit] = useState<CollapsedRowFit>(DEFAULT_FIT)
  const fitRef = useRef<CollapsedRowFit>(DEFAULT_FIT)

  const measure = useCallback(() => {
    const root = entryRef.current
    if (!root) return
    const main = root.querySelector<HTMLElement>('[data-row-fit="main"]')
    const titleText = root.querySelector<HTMLElement>('[data-row-fit="title-text"]')
    if (!main || !titleText) return

    const mainWidth = main.getBoundingClientRect().width
    if (mainWidth <= 0) return

    const checkWidth = root.querySelector<HTMLElement>('[data-row-fit="check"]')?.getBoundingClientRect().width ?? 0

    // The title may ellipsize down to MIN_TITLE_CHARS visible characters,
    // so only that minimum counts towards the required width (average
    // glyph width × MIN_TITLE_CHARS + ellipsis). Shorter titles count in
    // full — they never show an ellipsis.
    const titleWidth = titleText.getBoundingClientRect().width
    const titleChars = (titleText.textContent ?? '').length
    const titleRequired = titleChars > MIN_TITLE_CHARS
      ? Math.min(titleWidth, (titleWidth / titleChars) * MIN_TITLE_CHARS + ELLIPSIS_WIDTH)
      : titleWidth

    let iconsWidth = 0
    root.querySelectorAll<HTMLElement>('[data-row-fit="title-icon"]').forEach((el) => {
      iconsWidth += el.getBoundingClientRect().width + TITLE_ICON_MARGIN
    })
    const projectWidth = root.querySelector<HTMLElement>('[data-row-fit="project-badge"]')?.getBoundingClientRect().width ?? 0

    const timingsEl = root.querySelector<HTMLElement>('[data-row-fit="timings"]')
    const timingsShrunk = !!timingsEl && timingsEl.scrollWidth > timingsEl.clientWidth + 1
    // The rhythm text of the recurrence mark (#283) counts by its natural
    // width from the hidden measurer, never by whether it shows right now —
    // the visible one is taken back out of the timings cluster.
    const visibleText = root.querySelector<HTMLElement>('[data-row-fit="recurrence-text"]')?.getBoundingClientRect().width ?? 0
    const timingsWidth = Math.max(0, (timingsEl?.getBoundingClientRect().width ?? 0) - (visibleText > 0 ? visibleText + RECURRENCE_TEXT_GAP : 0))
    const textMeasure = root.querySelector<HTMLElement>('[data-recurrence-measure]')?.getBoundingClientRect().width ?? 0
    const recurrenceText = textMeasure > 0 ? textMeasure + RECURRENCE_TEXT_GAP : 0

    // Natural pill widths from the hidden measurers — independent of how
    // the visible cluster currently renders (expanded / summarized / shrunk).
    const tagsFull = root.querySelector<HTMLElement>('[data-tags-measure="full"]')?.getBoundingClientRect().width ?? 0
    const tagsSummary = root.querySelector<HTMLElement>('[data-tags-measure="summary"]')?.getBoundingClientRect().width ?? tagsFull

    const layout = timingsShrunk
      ? { twoLine: true, summarizeTags: true, recurrenceText: false }
      : computeRowLayout({
          available: mainWidth,
          base: checkWidth + iconsWidth + projectWidth + timingsWidth + FIXED_ROW_CHROME,
          titleRequired,
          titleFull: titleWidth,
          tagsFull,
          tagsSummary,
          timings: timingsWidth,
          row2Available: mainWidth - checkWidth - FIXED_ROW_CHROME,
          recurrenceText,
        }, !fitRef.current.fits, !fitRef.current.recurrenceText)

    // Without a rhythm text the flag means nothing — keep it, so rows without one never re-render over it.
    const next: CollapsedRowFit = {
      fits: !layout.twoLine,
      summarizeTags: layout.summarizeTags,
      recurrenceText: recurrenceText > 0 ? layout.recurrenceText : fitRef.current.recurrenceText,
    }
    if (next.fits !== fitRef.current.fits || next.summarizeTags !== fitRef.current.summarizeTags || next.recurrenceText !== fitRef.current.recurrenceText) {
      fitRef.current = next
      setFit(next)
    }
  }, [entryRef])

  // Measure before paint on mount/content changes (no wrong-layout flash),
  // then once more after badge width animations have settled.
  useLayoutEffect(() => {
    if (!enabled) return
    measure()
    const timer = setTimeout(measure, ANIMATION_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [enabled, contentKey, measure])

  useEffect(() => {
    if (!enabled) return
    const main = entryRef.current?.querySelector('[data-row-fit="main"]')
    if (!main) return
    const observer = new ResizeObserver(() => measure())
    observer.observe(main)
    return () => observer.disconnect()
  }, [enabled, measure, entryRef])

  return enabled ? fit : DEFAULT_FIT
}
